const MAX_SEQUENCE_LENGTH = 3;

/**
 * Evaluates password strength matching the React SPA criteria:
 *   - At least `minLength` characters (driven by backend policy via data attribute)
 *   - At least one lowercase letter
 *   - At least one uppercase letter
 *   - At least one number
 *
 * Strength levels: EMPTY → WEAK → FAIR → STRONG
 * FAIR requires all four criteria; STRONG additionally requires
 * length > minLength, multiple uppercase chars, and no sequential digit runs.
 *
 * NOTE: This strength meter is intentionally stricter than the backend
 * AccountPasswordSecurityValidator, which accepts passwords meeting only
 * 2 of 3 character types. The meter encourages stronger passwords as
 * aspirational guidance; the server enforces the minimum policy.
 */
const containsOrderedDigitSequence = (password) => {
  const regex = new RegExp(String.raw`\d{${MAX_SEQUENCE_LENGTH},}`, 'g');
  const groups = password.match(regex) ?? [];

  return groups.some((group) => {
    const digits = [...group].map(Number);
    return digits.slice(0, -(MAX_SEQUENCE_LENGTH - 1)).some((d, i) => {
      const step = digits[i + 1] - d;
      return Math.abs(step) === 1 && digits[i + 2] - digits[i + 1] === step;
    });
  });
};

/** @param {string} password @param {number} minLength */
export const evaluateStrength = (password, minLength) => {
  if (password.length === 0) {
    const criteria = {
      isLongEnough: false,
      hasLowercase: false,
      hasUppercase: false,
      hasNumber: false,
    };
    return { criteria, strength: 'EMPTY', progress: 0 };
  }

  // \p{Lu}/\p{Ll} (Unicode property escapes) match AccountPasswordSecurityValidator's
  // server-side rule, which counts any Unicode uppercase/lowercase letter, not just
  // ASCII — a Cyrillic or accented password should gate the same way it validates.
  const uppercaseMatches = password.match(/\p{Lu}/gu) || [];

  // password.length counts UTF-16 code units, so a surrogate-pair character
  // (e.g. an emoji) counts as 2 — Ruby's String#length (what the backend
  // validates against) counts 1. [...password] iterates by code point,
  // matching the server's count.
  const codePointLength = [...password].length;

  const criteria = {
    isLongEnough: codePointLength >= minLength,
    hasLowercase: /\p{Ll}/u.test(password),
    hasUppercase: uppercaseMatches.length > 0,
    hasNumber: /\d/.test(password),
  };

  const metCount = Object.values(criteria).filter(Boolean).length;

  if (metCount < 4) {
    const progress = metCount < 3 ? 25 : 50;
    return { criteria, strength: 'WEAK', progress };
  }

  const hasSequence = containsOrderedDigitSequence(password);
  const isStrong = codePointLength > minLength
    && uppercaseMatches.length > 1
    && !hasSequence;

  return isStrong
    ? { criteria, strength: 'STRONG', progress: 100 }
    : { criteria, strength: 'FAIR', progress: 75 };
};

const STRENGTH_COLORS = {
  0: '#a3a9b9',
  25: '#a3a9b9',
  50: '#eb4b4b',
  75: '#e99921',
  100: '#14803c',
};

/**
 * Checklist mode's 4-segment meter collapses the tooltip mode's WEAK bucket
 * (which covers both "not long enough" and "long enough but missing a
 * character type") into two distinct tiers, using the same `criteria`/
 * `strength` evaluateStrength already computed — no separate evaluation
 * logic, just a different display grouping.
 */
const SEGMENT_TIERS = {
  EMPTY: { count: 0, labelKey: null, color: '' },
  TOO_SHORT: { count: 1, labelKey: 'strengthTooShort', color: STRENGTH_COLORS[50] },
  WEAK: { count: 2, labelKey: 'strengthWeak', color: STRENGTH_COLORS[50] },
  FAIR: { count: 3, labelKey: 'strengthFair', color: STRENGTH_COLORS[100] },
  STRONG: { count: 4, labelKey: 'strengthStrong', color: STRENGTH_COLORS[100] },
};

const segmentTierFor = (criteria, strength) => {
  if (strength === 'EMPTY') return 'EMPTY';
  if (!criteria.isLongEnough) return 'TOO_SHORT';
  if (strength === 'WEAK') return 'WEAK';
  return strength; // FAIR or STRONG
};

const setCriterionVisibility = (tooltip, key, met, checklistMode) => {
  const el = tooltip.querySelector(`[data-criterion="${CSS.escape(key)}"]`);
  if (!el) return;

  if (checklistMode) {
    const metClass = 'devise-form-wrapper__password-criteria-item_met';
    const wasMet = el.classList.contains(metClass);
    if (met === wasMet) return; // Same state: skip re-announcing on every keystroke.

    el.classList.toggle(metClass, met);

    // The bullet/check swap above is purely visual; this text change is what
    // the item's aria-live ancestor actually announces to screen readers.
    // Both labels are non-empty so a regression (met -> unmet) is announced
    // too — the aria-relevant default ("additions text") won't announce a
    // removal down to an empty string, only a change to new, present text.
    // The live region is aria-atomic="false" (the default), so only this
    // span's own text is read out — it repeats the requirement's name
    // alongside the state so the announcement identifies which of the four
    // requirements changed, instead of a context-free "(met)".
    const status = el.querySelector('[data-criterion-status]');
    const state = met ? tooltip.dataset.metLabel : tooltip.dataset.unmetLabel;
    if (status) status.textContent = `${el.dataset.criterionLabel} — ${state}`;
  } else {
    el.style.display = met ? 'none' : 'list-item';
  }
};

/**
 * Shows/hides the criteria tooltip and each requirement inside it — or, in
 * "checklist" mode, leaves it visible from page load (CSS default) and marks
 * each requirement met instead of hiding it.
 */
const updateCriteriaTooltip = (tooltip, criteria, strength) => {
  const el = tooltip;
  const checklistMode = el.dataset.criteriaDisplay === 'checklist';
  if (!checklistMode) {
    el.style.display = strength === 'WEAK' ? 'block' : 'none';
  }

  const items = {
    length: criteria.isLongEnough,
    lowercase: criteria.hasLowercase,
    uppercase: criteria.hasUppercase,
    number: criteria.hasNumber,
  };

  for (const [key, met] of Object.entries(items)) {
    setCriterionVisibility(tooltip, key, met, checklistMode);
  }
};

const updateSingleBar = (elements, field, strength, progress) => {
  const { progressBar, strengthLabel, labelText } = elements;
  const color = STRENGTH_COLORS[progress];

  progressBar.style.width = `${progress}%`;
  progressBar.style.backgroundColor = color;

  const labelKeys = {
    EMPTY: '',
    WEAK: field.dataset.strengthWeak,
    FAIR: field.dataset.strengthFair,
    STRONG: field.dataset.strengthStrong,
  };

  labelText.textContent = labelKeys[strength] || '';
  strengthLabel.style.color = color;
  strengthLabel.style.visibility = strength === 'EMPTY' ? 'hidden' : 'visible';
};

const updateSegments = (elements, field, criteria, strength) => {
  const {
    segments, strengthLabel, labelText, strongHint,
  } = elements;
  const tier = segmentTierFor(criteria, strength);
  const { count, labelKey, color } = SEGMENT_TIERS[tier];

  for (const [index, segment] of segments.entries()) {
    segment.style.backgroundColor = index < count ? color : '';
  }

  labelText.textContent = labelKey ? field.dataset[labelKey] : '';
  strengthLabel.style.color = color;
  strengthLabel.style.visibility = tier === 'EMPTY' ? 'hidden' : 'visible';

  // The hint explains what's still missing to reach STRONG. Whether the field
  // is currently focused is handled declaratively in CSS (:focus-within), not
  // here — this only marks eligibility (currently at the FAIR/"good" tier),
  // since that's the one part of the condition JS actually has to compute.
  if (strongHint) {
    strongHint.classList.toggle('devise-form-wrapper__tooltip-bubble--eligible', tier === 'FAIR');
  }
};

/**
 * Initializes the password strength meter:
 * - Evaluates password on each input
 * - Updates either the single progress bar (tooltip mode) or the 4 discrete
 *   segments (checklist mode), whichever is present
 * - Shows the strength label (Weak / Fair / Strong, or checklist mode's
 *   too short / weak / good / strong)
 */
export const initPasswordStrength = () => {
  const field = /** @type {HTMLInputElement} */ (
    document.getElementById('password-field')
  );
  if (!field) return;

  const strengthLabel = document.getElementById('password-strength-label');
  const labelText = document.getElementById('password-strength-label-text');
  const strongHint = document.getElementById('password-strength-strong-hint-bubble');
  const progressBar = document.getElementById('password-strength-progress');
  const segments = document.querySelectorAll(
    '#password-strength-segments .devise-form-wrapper__password-strength-segment',
  );
  if (!strengthLabel || !labelText || (!progressBar && segments.length === 0)) return;

  const elements = {
    progressBar, strengthLabel, labelText, strongHint, segments,
  };
  const tooltip = document.getElementById('password-criteria-tooltip');
  const minLength = Number(field.dataset.minLength);

  field.addEventListener('input', () => {
    const { criteria, strength, progress } = evaluateStrength(field.value, minLength);

    if (segments.length > 0) {
      updateSegments(elements, field, criteria, strength);
    } else {
      updateSingleBar(elements, field, strength, progress);
    }

    if (tooltip) updateCriteriaTooltip(tooltip, criteria, strength);
  });
};
